(() => {
  const ROOT_SELECTOR = '[data-cloth-product-rows]';
  const CARD_SELECTOR = '[data-cloth-size-card]';
  const OPTION_SELECTOR = '[data-cloth-color-option]';
  const OPTIONS_CONTAINER_SELECTOR = '[data-cloth-color-options]';
  const IMAGE_SELECTOR = '[data-cloth-main-image]';
  const LABEL_SELECTOR = '[data-cloth-selected-color]';
  const BUY_LINK_SELECTOR = '[data-cloth-buy-link]';

  if (window.__clothCardVersion4Bound) return;
  window.__clothCardVersion4Bound = true;

  function getOption(target) {
    return target instanceof Element ? target.closest(OPTION_SELECTOR) : null;
  }

  function getCard(option) {
    return option ? option.closest(CARD_SELECTOR) : null;
  }

  // Shared hover mechanics - decode-gated image swaps, the group-wide leave
  // rule, hover/focus-visible gating. See assets/bc-hover-preview.js.
  const HP = window.BCHoverPreview;

  // The group used to be this swatch grid, which held the preview while
  // sliding between swatches but still reset on the way to a size button -
  // those sit in a different container, so crossing between them flashed the
  // base variant. The group is the whole card now (bc-hover-preview owns the
  // selector), so every control on the card previews into every other one
  // directly.
  function leavesCard(from, relatedTarget) {
    return HP ? HP.leavesGroup(from, relatedTarget) : true;
  }

  function optionBelongsToRoot(option) {
    return Boolean(option && option.closest(ROOT_SELECTOR));
  }

  // `immediate` is used by the restore path: leaving the card has to put the
  // photo, the label and the price back in one synchronous write, so a decode
  // cannot leave a half-restored card behind.
  function applyOption(card, option, immediate) {
    if (!card || !option) return;

    const image = card.querySelector(IMAGE_SELECTOR);
    const label = card.querySelector(LABEL_SELECTOR);
    const colorName = option.dataset.colorName || '';
    const imageSrc = option.dataset.imageSrc;
    const imageAlt = option.dataset.imageAlt || colorName;

    if (image && imageSrc) {
      const data = {
        src: imageSrc,
        srcset: option.dataset.imageSrcset,
        sizes: image.getAttribute('sizes'),
        alt: imageAlt
      };

      if (HP) {
        // Decode before the swap: the previous colour stays up until the new
        // file is ready to paint, and a stale swap from a fast sweep is
        // dropped by the token rather than landing late.
        HP.swapImage(image, data, { group: HP.group(card), immediate: Boolean(immediate) });
      } else {
        if (data.srcset) {
          if (image.getAttribute('srcset') !== data.srcset) image.srcset = data.srcset;
        } else if (image.hasAttribute('srcset')) {
          image.removeAttribute('srcset');
        }
        if (image.getAttribute('src') !== data.src) image.src = data.src;
        image.alt = data.alt;
      }
    }

    if (label) {
      label.textContent = colorName;
      label.title = colorName;
    }

    // Per-option price swap: only options that carry data-price (ball
    // configurator) trigger this; cloth color options have no such
    // attribute, so cloth behavior is unchanged.
    const price = option.dataset.price;
    if (price) {
      const priceHost = option.closest('[data-simonis-family-card]') || card;
      const priceEl = priceHost.querySelector('[data-cloth-price]');
      if (HP) HP.setText(priceEl, price);
      else if (priceEl) priceEl.textContent = price;
    }
  }


  function updateBuyLink(card, option) {
    if (!card || !option) return;

    const buyLink = card.querySelector(BUY_LINK_SELECTOR);
    const productUrl = option.dataset.productUrl;

    if (!buyLink) return;

    if (!productUrl) {
      buyLink.removeAttribute('href');
      buyLink.removeAttribute('data-selected-product-url');
      buyLink.setAttribute('aria-disabled', 'true');
      buyLink.classList.add('is-disabled');
      return;
    }

    buyLink.href = productUrl;
    buyLink.dataset.selectedProductUrl = productUrl;
    buyLink.removeAttribute('aria-disabled');
    buyLink.classList.remove('is-disabled');
  }

  function clearPreview(card) {
    if (!card) return;

    card.querySelectorAll(OPTION_SELECTOR).forEach((option) => {
      option.classList.remove('is-preview');
    });
  }

  function getSelectedOption(card) {
    if (!card) return null;

    return (
      card.querySelector(OPTION_SELECTOR + '.is-active') ||
      card.querySelector(OPTION_SELECTOR)
    );
  }

  function previewOption(option) {
    if (!optionBelongsToRoot(option)) return;

    const card = getCard(option);
    if (!card) return;

    clearPreview(card);
    option.classList.add('is-preview');
    applyOption(card, option);
  }

  function restoreSelected(card) {
    if (!card) return;

    clearPreview(card);
    const selected = getSelectedOption(card);

    // Synchronous: one write back to the selected variant, never decode-gated.
    if (selected) {
      applyOption(card, selected, true);
    }
  }

  // The pointer left the card, so the panel on show goes back to its own
  // selected swatch.
  //
  // Only the ACTIVE panel, not all of them. A card holds one panel per size
  // but they all share a single price element, so restoring every panel wrote
  // that element once per panel and the figure that stuck was whichever panel
  // came last in the DOM rather than the one the customer is looking at -
  // measured on the Tweeten tips card (7 panels, 3 competing writes on a
  // single leave).
  function restoreCardGroup(node) {
    const owner = HP ? HP.group(node) : null;
    if (!owner) {
      restoreSelected(node instanceof Element ? node.closest(CARD_SELECTOR) : null);
      return;
    }

    const panels = [...owner.querySelectorAll(CARD_SELECTOR)];
    const active = panels.filter((panel) => !panel.hidden && panel.classList.contains('is-active'));
    (active.length ? active : panels.slice(0, 1)).forEach(restoreSelected);
  }

  function selectOption(option) {
    if (!optionBelongsToRoot(option)) return;

    const card = getCard(option);
    if (!card) return;

    card.querySelectorAll(OPTION_SELECTOR).forEach((item) => {
      const selected = item === option;
      item.classList.toggle('is-active', selected);
      item.classList.remove('is-preview');
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
    });

    applyOption(card, option);
    updateBuyLink(card, option);
  }

  function initialize(root = document) {
    root.querySelectorAll(ROOT_SELECTOR + ' ' + CARD_SELECTOR).forEach((card) => {
      if (card.dataset.clothCardInitialized === 'true') return;

      const selected = getSelectedOption(card);
      if (!selected) return;

      card.dataset.clothCardInitialized = 'true';
      selectOption(selected);

      // Warm the candidate the browser will actually paint, not just the
      // src. These images carry a srcset, so the displayed file is chosen
      // from it by width - a different URL from data-image-src. Preloading
      // only the src downloaded one size (measured: 133KB over 3.3s) while
      // the one that gets rendered had never been fetched, so the first
      // hover still went to the network and flashed on the way.
      //
      // Assign sizes and srcset before src: the selection algorithm reads
      // them at the moment src is set, so the order is what makes the
      // preload pick the same candidate as the real element.
      const cardImage = card.querySelector(IMAGE_SELECTOR);
      const imageSizes = cardImage ? cardImage.getAttribute('sizes') : null;

      card.querySelectorAll(OPTION_SELECTOR).forEach((option) => {
        const imageSrc = option.dataset.imageSrc;
        if (!imageSrc) return;

        const preload = new Image();
        if (imageSizes) preload.sizes = imageSizes;
        if (option.dataset.imageSrcset) preload.srcset = option.dataset.imageSrcset;
        preload.src = imageSrc;
      });
    });
  }

  // Hover only where a hover exists. On touch the tap is a click and the
  // click handler below owns it; running the hover path there as well was
  // the second half of the flicker on phones.
  document.addEventListener('pointerover', (event) => {
    if (HP && !HP.hoverCapable()) return;

    const option = getOption(event.target);
    if (!option) return;

    previewOption(option);
  });

  // The one revert: the pointer leaves the swatch GRID - pointerleave on
  // the grid element, served by bc-hover-preview.js for every row. Not a
  // swatch, not the gap between two (the gap is the grid's own box), and
  // not the card, which kept the last colour hovered on show all the way
  // down to BUY (2026-09-10).
  if (HP) HP.onZoneLeave(OPTIONS_CONTAINER_SELECTOR, (zone) => { restoreCardGroup(zone); });

  document.addEventListener('focusin', (event) => {
    const option = getOption(event.target);
    if (!option) return;
    // Keyboard focus previews; the focus a pointer press leaves behind does
    // not - the click has already made the selection.
    if (HP && !HP.focusVisible(option)) return;

    previewOption(option);
  });

  document.addEventListener('focusout', (event) => {
    if (!(event.target instanceof Element)) return;

    const option = getOption(event.target);
    if (!option) return;
    if (HP && !HP.focusVisible(option)) return;
    // Same rule as the pointer: tabbing between controls of one card keeps
    // the preview, leaving the card restores it.
    if (!leavesCard(event.target, event.relatedTarget)) return;

    restoreCardGroup(event.target);
  });

  document.addEventListener('click', (event) => {
    const option = getOption(event.target);
    if (!option) return;

    event.preventDefault();
    selectOption(option);
  });

  document.addEventListener('keydown', (event) => {
    const option = getOption(event.target);
    if (!option) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectOption(option);
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    initialize(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  window.BCClothCardVersion4 = {
    applyOption,
    initialize,
    updateBuyLink,
    previewOption,
    restoreSelected,
    selectOption
  };

  if (document.readyState !== 'loading') {
    initialize(document);
  }
})();
